import { Injectable, Injector, ApplicationRef, ComponentFactoryResolver, ComponentRef } from "@angular/core";
import { ToolbarConfigService } from "../../nav-data/tool-bar";
import { CommonFileNavTreeComponent } from "./common-file-nav-tree/common-file-nav-tree";
import { TabBarToolbar } from "./tool-bar/tool-bar";
import { CommonFileNavTreeDataService } from '../../nav-data/common-file';
import { NavTreeNodeIconService } from '../../nav-data/node-icon';
import { NavTreeSearchBarConfig } from "../../interface/nav-panel";
import { NavTreeNode } from '../../interface/tree';
import { NODE_CONTENT_FIELD } from '../../components/tree/tree';
import { escapeTextForBrowser } from '../../utils/escape';

/**
 * 导航树面板工厂
 * @remarks 用于封装一些导航面板相关组件的工厂方法
 */
@Injectable()
export class NavPanelFactory {

  constructor(
    private applicationRef: ApplicationRef,
    private resolver: ComponentFactoryResolver
  ) { }

  /**
   * 创建一个文件导航树组件（直接传入导航树数据服务）
   * @param injector 注入器
   * @param dataService 导航树数据服务
   * @param iconService 导航树图标服务
   * @param isMainNavTree 是否主要导航树，主要导航树会随着当前页签的变化而变化自己的当前选中节点
   * @param emptyMessage 空状态提示语句
   * @param searchBarConfig 搜索栏配置
   * @param virtualized 是否启用虚拟列表，默认不启用
   * @param disableSearchService 是否禁用搜索服务，默认不禁用
   * @returns 文件导航树组件
   */
  public createCommonFileNav(
    injector: Injector,
    dataService: CommonFileNavTreeDataService,
    iconService?: NavTreeNodeIconService,
    isMainNavTree: boolean = true,
    emptyMessage?: string,
    searchBarConfig?: NavTreeSearchBarConfig,
    virtualized: boolean = false,
    disableSearchService: boolean = false
  ): ComponentRef<CommonFileNavTreeComponent> {
    const cmpFactory = this.resolver.resolveComponentFactory(CommonFileNavTreeComponent);
    const cmpRef = cmpFactory.create(injector);
    const instance = cmpRef.instance;
    if (dataService) {
      dataService['getChildrenWithValidId'] = async (rootPath?: string, curPath?: string): Promise<NavTreeNode[]> => {
        const nodes = await dataService.getChildren(rootPath, curPath);
        for (const node of nodes) {
          traverseTree(node, (n) => {
            n.id = getValidId(n.id);
            if (!n.data) {
              return;
            }
            n.data.id = n.id;
            const name = n.data && n.data.name || '';
            n.data[NODE_CONTENT_FIELD] = escapeTextForBrowser(name);
          });
        }
        return nodes;
      }
    }
    instance.setDataService(dataService, iconService);
    instance.isMainNavTree = isMainNavTree;
    instance.emptyMessage = emptyMessage;
    instance.searchBarConfig = searchBarConfig;
    instance.virtualized = virtualized;
    instance.disableSearchService = disableSearchService;
    setTimeout(() => { this.applicationRef.attachView(cmpRef.hostView); });
    return cmpRef;
  }

  /**
   * 创建一个导航面板工具栏组件
   * @param injector 注入器
   * @returns 工具栏组件
   */
  public createToolbar(
    injector: Injector,
    configService: ToolbarConfigService
  ): ComponentRef<TabBarToolbar> {
    const cmpFactory = this.resolver.resolveComponentFactory(TabBarToolbar);
    const cmpRef = cmpFactory.create(injector);
    cmpRef.instance.setConfigService(configService);
    setTimeout(() => { this.applicationRef.attachView(cmpRef.hostView); });
    return cmpRef;
  }

}

/**
 * 递归遍历树
 * @param node 树节点
 * @param handleNode 处理每一个树节点
 */
function traverseTree(
  node: NavTreeNode,
  handleNode: (node: NavTreeNode) => void
): void {
  if (!node) {
    return;
  }
  handleNode(node);
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      traverseTree(child, handleNode);
    }
  }
}

/**
 * 去除标识串中作为id的非法字符，仅保留大小写英文字母、数字、中划线、下划线
 * @param id 标识串
 */
function getValidId(id: string): string {
  return id.split('').filter((char) => {
    const regExp = /^[0-9a-zA-Z-_]{1}$/g;
    return regExp.test(char);
  }).join('');
}
